import React, { useState, useRef, useCallback, useEffect } from "react";
import { api } from "@/lib/api";
import { detectAdRequired } from "@/lib/ads";
import RewardedAdModal from "@/components/RewardedAdModal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Compass, Upload, Camera, RotateCw, Home, LandPlot, Sparkles, AlertCircle, CheckCircle2, Crosshair } from "lucide-react";

/**
 * Vastu Analyzer — upload a floor plan or empty plot image, drag the compass
 * dial to indicate where North is, submit for AI-powered Vastu compliance.
 *
 * Flow:
 *   Step 1: Pick mode (Floor Plan / Plot)
 *   Step 2: Upload image (camera or gallery)
 *   Step 3: Rotate the dial so the red North arrow points to actual North in the image
 *   Step 4: Submit → see annotated overlay + compliance report
 */
export default function Vastu() {
  const [mode, setMode] = useState("floor_plan");
  const [imageB64, setImageB64] = useState(null);           // raw base64 (no data: prefix)
  const [imageUrl, setImageUrl] = useState(null);           // data: URL for preview
  const [northAngle, setNorthAngle] = useState(0);          // 0..360 clockwise from top
  const [center, setCenter] = useState({ x: 0.5, y: 0.5 }); // Brahma Sthan centre (image-fraction)
  const [draggingCenter, setDraggingCenter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [adOpen, setAdOpen] = useState(false);
  const pendingRetryRef = useRef(null);

  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const dialRef = useRef(null);
  const imageWrapRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Image must be under 8 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImageUrl(dataUrl);
      setImageB64(dataUrl.split(",", 2)[1]);
      setNorthAngle(0);
      setCenter({ x: 0.5, y: 0.5 });
      setResult(null);
      setError("");
    };
    reader.readAsDataURL(file);
  };

  // --- Rotatable compass dial ---
  const pointerFromEvent = useCallback((ev) => {
    const el = dialRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const t = ev.touches?.[0] || ev;
    const x = t.clientX - cx;
    const y = t.clientY - cy;
    // atan2 returns radians from +X axis. We want 0° at TOP, clockwise.
    const deg = (Math.atan2(y, x) * 180) / Math.PI + 90;
    return ((deg % 360) + 360) % 360;
  }, []);

  const [dragging, setDragging] = useState(false);
  const onPointerDown = (ev) => {
    ev.preventDefault();
    setDragging(true);
    const a = pointerFromEvent(ev);
    if (a != null) setNorthAngle(a);
  };
  useEffect(() => {
    if (!dragging) return undefined;
    const move = (ev) => {
      const a = pointerFromEvent(ev);
      if (a != null) setNorthAngle(a);
    };
    const up = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, pointerFromEvent]);

  const nudge = (delta) => setNorthAngle((a) => (a + delta + 360) % 360);
  const resetNorth = () => setNorthAngle(0);

  // ---- Draggable Brahma Sthan centre marker ----
  const centerFromEvent = useCallback((ev) => {
    const wrap = imageWrapRef.current;
    if (!wrap) return null;
    const rect = wrap.getBoundingClientRect();
    const t = ev.touches?.[0] || ev;
    let x = (t.clientX - rect.left) / rect.width;
    let y = (t.clientY - rect.top) / rect.height;
    x = Math.max(0.15, Math.min(0.85, x));
    y = Math.max(0.15, Math.min(0.85, y));
    return { x, y };
  }, []);

  const onCenterDown = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setDraggingCenter(true);
    const c = centerFromEvent(ev);
    if (c) setCenter(c);
  };
  useEffect(() => {
    if (!draggingCenter) return undefined;
    const move = (ev) => {
      const c = centerFromEvent(ev);
      if (c) setCenter(c);
    };
    const up = () => setDraggingCenter(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [draggingCenter, centerFromEvent]);
  const resetCenter = () => setCenter({ x: 0.5, y: 0.5 });

  const submit = async () => {
    if (!imageB64) { setError("Please upload an image first."); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/vastu/analyze", {
        image_base64: imageB64,
        north_angle_deg: northAngle,
        mode,
        center_x_frac: center.x,
        center_y_frac: center.y,
      });
      setResult(data);
    } catch (err) {
      const adKind = detectAdRequired(err);
      if (adKind === "vastu") {
        // Vastu-specific ad gate — remember we need to retry after reward.
        pendingRetryRef.current = () => submit();
        setAdOpen(true);
      } else {
        setError(err?.response?.data?.detail?.message || err?.response?.data?.detail || err.message || "Vastu analysis failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const onRewarded = () => {
    setAdOpen(false);
    const retry = pendingRetryRef.current;
    pendingRetryRef.current = null;
    if (retry) retry();
  };

  const scoreColor = (s) => s >= 80 ? "text-emerald-300" : s >= 60 ? "text-amber-300" : "text-rose-300";

  return (
    <div className="space-y-6" data-testid="vastu-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Vastu Analyzer</h1>
        <p className="text-slate-400">
          Upload a floor plan or empty plot image, set North on the compass, and get a classical 16-direction Vastu Purusha Mandala compliance report powered by AI.
        </p>
      </header>

      {!result && (
        <Card className="glass border-white/10 p-6" data-testid="vastu-input-card">
          {/* Mode selector */}
          <div className="mb-6">
            <Label className="text-slate-300">What are you analysing?</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button
                type="button"
                onClick={() => setMode("floor_plan")}
                data-testid="vastu-mode-floor"
                className={`p-4 rounded-xl border transition flex items-start gap-3 text-left ${
                  mode === "floor_plan" ? "bg-amber-500/15 border-amber-400/60 text-amber-200" : "bg-slate-900/40 border-white/10 text-slate-300 hover:border-white/20"
                }`}
              >
                <Home className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Floor Plan</div>
                  <div className="text-xs mt-1 opacity-80">Already have a plan drawn with rooms marked? Get room-by-room Vastu scoring + remedies.</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode("plot")}
                data-testid="vastu-mode-plot"
                className={`p-4 rounded-xl border transition flex items-start gap-3 text-left ${
                  mode === "plot" ? "bg-amber-500/15 border-amber-400/60 text-amber-200" : "bg-slate-900/40 border-white/10 text-slate-300 hover:border-white/20"
                }`}
              >
                <LandPlot className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Empty Plot / Map</div>
                  <div className="text-xs mt-1 opacity-80">Analysing a plot before construction? Get an ideal room-placement plan + shape analysis.</div>
                </div>
              </button>
            </div>
          </div>

          {/* Image upload */}
          {!imageUrl ? (
            <div className="border-2 border-dashed border-white/10 rounded-xl p-10 text-center">
              <Compass className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <p className="text-slate-300 mb-4">Upload a {mode === "plot" ? "plot / map" : "floor plan"} image</p>
              <div className="flex gap-3 justify-center">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="vastu-file-input" />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="vastu-camera-input" />
                <Button onClick={() => fileRef.current?.click()} data-testid="vastu-upload-btn" variant="outline" className="rounded-full border-white/20 text-slate-200">
                  <Upload className="w-4 h-4 mr-2" /> Choose from gallery
                </Button>
                <Button onClick={() => cameraRef.current?.click()} data-testid="vastu-capture-btn" variant="outline" className="rounded-full border-white/20 text-slate-200">
                  <Camera className="w-4 h-4 mr-2" /> Take photo
                </Button>
              </div>
              <p className="text-xs text-slate-600 mt-4">PNG / JPG / WebP under 8 MB</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Image preview with draggable Brahma Sthan centre */}
                <div
                  ref={imageWrapRef}
                  className="relative rounded-xl overflow-hidden bg-slate-900/50 border border-white/5 touch-none select-none"
                  data-testid="vastu-image-wrap"
                >
                  <img src={imageUrl} alt="Floor plan preview" data-testid="vastu-preview-image" className="w-full h-auto block pointer-events-none" />
                  {/* Centre marker — draggable crosshair */}
                  <div
                    onMouseDown={onCenterDown}
                    onTouchStart={onCenterDown}
                    data-testid="vastu-center-marker"
                    className="absolute flex items-center justify-center cursor-grab active:cursor-grabbing"
                    style={{
                      left: `${center.x * 100}%`,
                      top: `${center.y * 100}%`,
                      transform: "translate(-50%, -50%)",
                      width: 48, height: 48,
                    }}
                  >
                    <div className="absolute inset-0 rounded-full border-2 border-amber-400 bg-amber-500/15 pointer-events-none" />
                    <Crosshair className="w-5 h-5 text-amber-300 pointer-events-none" />
                  </div>
                  <div className="absolute left-2 top-2 bg-slate-950/80 text-amber-300 text-xs px-2 py-1 rounded-full pointer-events-none">
                    Drag the ◎ marker to the centre of your plan
                  </div>
                </div>

                {/* Compass dial + precise angle input */}
                <div className="flex flex-col items-center justify-center">
                  <div className="text-sm text-slate-300 mb-3 text-center">
                    Drag the <span className="text-rose-300 font-medium">RED (N)</span> arrow to point to actual North in your image.
                  </div>
                  <div
                    ref={dialRef}
                    data-testid="vastu-compass-dial"
                    onMouseDown={onPointerDown}
                    onTouchStart={onPointerDown}
                    className="relative w-56 h-56 rounded-full border-2 border-amber-400/60 bg-slate-950 touch-none select-none cursor-grab active:cursor-grabbing shadow-[0_0_40px_rgba(251,191,36,0.3)]"
                    style={{ userSelect: "none" }}
                  >
                    <div
                      className="absolute left-1/2 top-1/2 w-1 h-24 bg-rose-500 rounded-full"
                      style={{ transform: `translate(-50%, -100%) rotate(${northAngle}deg)`, transformOrigin: "bottom center" }}
                    />
                    <div
                      className="absolute left-1/2 top-1/2 flex items-center justify-center w-6 h-6 rounded-full bg-rose-500 border-2 border-white font-bold text-slate-950"
                      style={{ transform: `translate(-50%, -100%) rotate(${northAngle}deg) translateY(-96px) rotate(-${northAngle}deg)`, transformOrigin: "bottom center" }}
                    >
                      N
                    </div>
                    <div
                      className="absolute left-1/2 top-1/2 flex items-center justify-center w-5 h-5 rounded-full bg-slate-600 text-xs text-slate-200"
                      style={{ transform: `translate(-50%, -100%) rotate(${northAngle + 180}deg) translateY(-82px) rotate(-${northAngle + 180}deg)`, transformOrigin: "bottom center" }}
                    >
                      S
                    </div>
                    <div className="absolute left-1/2 top-1/2 w-3 h-3 bg-amber-400 rounded-full -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute inset-x-0 bottom-3 text-center text-xs text-slate-400">
                      North @ <span data-testid="vastu-angle-readout" className="text-amber-300 font-mono">{Math.round(northAngle)}°</span>
                    </div>
                  </div>

                  {/* Precise angle controls */}
                  <div className="mt-4 flex items-center gap-2 flex-wrap justify-center">
                    <Label htmlFor="vastu-angle-input" className="text-xs text-slate-400">Precise:</Label>
                    <Input
                      id="vastu-angle-input"
                      type="number"
                      min="0" max="360" step="0.5"
                      value={Math.round(northAngle * 10) / 10}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isNaN(v)) setNorthAngle(((v % 360) + 360) % 360);
                      }}
                      data-testid="vastu-angle-input"
                      className="h-8 w-20 bg-slate-900/50 border-white/10 text-amber-300 text-center font-mono"
                    />
                    <span className="text-xs text-slate-500">°</span>
                    <Button type="button" onClick={() => nudge(-15)} variant="outline" size="sm" data-testid="vastu-angle-dec" className="rounded-full border-white/10 h-8 w-8 p-0">−</Button>
                    <Button type="button" onClick={resetNorth} variant="outline" size="sm" data-testid="vastu-angle-reset" className="rounded-full border-white/10 h-8 gap-1">
                      <RotateCw className="w-3 h-3" /> Reset
                    </Button>
                    <Button type="button" onClick={() => nudge(15)} variant="outline" size="sm" data-testid="vastu-angle-inc" className="rounded-full border-white/10 h-8 w-8 p-0">+</Button>
                  </div>
                  <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
                    Centre: <span className="font-mono text-amber-300">x={Math.round(center.x * 100)}%</span>
                    <span className="font-mono text-amber-300">y={Math.round(center.y * 100)}%</span>
                    <button type="button" onClick={resetCenter} data-testid="vastu-center-reset" className="underline text-slate-500 hover:text-amber-300">reset centre</button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                <Button onClick={() => { setImageUrl(null); setImageB64(null); }} variant="ghost" data-testid="vastu-clear-btn" className="text-slate-400 hover:text-slate-200">
                  Choose different image
                </Button>
                <Button onClick={submit} disabled={loading} data-testid="vastu-analyze-btn" className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 px-6">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing your Vastu...</> : <>Analyze Vastu <Sparkles className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>
            </div>
          )}

          {error && <div className="mt-4 text-sm text-rose-400" data-testid="vastu-error">{error}</div>}
        </Card>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-6" data-testid="vastu-result">
          <Card className="glass border-white/10 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h3 className="font-serif text-2xl text-slate-50">Vastu Compliance Report</h3>
                <p className="text-sm text-slate-400 mt-1">{result.mode === "plot" ? "Empty plot analysis" : "Floor plan analysis"} · North @ {Math.round(result.north_angle_deg)}°</p>
              </div>
              <div className="text-right">
                <div className={`font-serif text-5xl ${scoreColor(result.report?.score || 0)}`} data-testid="vastu-score">
                  {result.report?.score ?? 0}
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Score / 100</div>
              </div>
            </div>
            <img src={result.annotated_image} alt="Annotated Vastu overlay" data-testid="vastu-annotated-image" className="w-full rounded-xl border border-white/5 mb-4" />
            <p className="text-slate-200 leading-relaxed" data-testid="vastu-summary">{result.report?.summary}</p>
          </Card>

          {result.report?.rooms?.length > 0 && (
            <Card className="glass border-white/10 p-6" data-testid="vastu-rooms">
              <h4 className="font-serif text-xl text-slate-50 mb-3">Rooms Detected</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.report.rooms.map((r, i) => (
                  <div key={i} className={`p-3 rounded-xl border ${r.verdict === "ideal" ? "bg-emerald-500/5 border-emerald-400/30" : r.verdict === "poor" ? "bg-rose-500/5 border-rose-400/30" : "bg-slate-900/40 border-white/10"}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-medium text-slate-100 capitalize">{r.name}</div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-950/50 text-amber-300">{r.zone}</span>
                    </div>
                    <div className={`text-xs mt-1 ${r.verdict === "ideal" ? "text-emerald-300" : r.verdict === "poor" ? "text-rose-300" : "text-slate-400"}`}>
                      {r.verdict?.toUpperCase()} · {r.note}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {result.report?.placement_plan?.length > 0 && (
            <Card className="glass border-white/10 p-6" data-testid="vastu-placement-plan">
              <h4 className="font-serif text-xl text-slate-50 mb-3">Ideal Room Placement</h4>
              <div className="space-y-2">
                {result.report.placement_plan.map((p, i) => (
                  <div key={i} className="p-3 rounded-xl bg-slate-900/40 border border-white/10 flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-slate-100 capitalize">{p.room}</span>
                      <span className="text-xs ml-2 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">{p.zone}</span>
                      <p className="text-xs text-slate-400 mt-1">{p.reason}</p>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {result.report?.violations?.length > 0 && (
            <Card className="glass border-white/10 p-6" data-testid="vastu-violations">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-rose-400" />
                <h4 className="font-serif text-xl text-slate-50">Violations & Remedies</h4>
              </div>
              <div className="space-y-3">
                {result.report.violations.map((v, i) => (
                  <div key={i} className="p-3 rounded-xl bg-rose-500/5 border border-rose-400/20">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="font-medium text-rose-200">{v.issue}</div>
                      {v.zone && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300">{v.zone}</span>}
                    </div>
                    <div className="text-sm text-slate-300 mt-2">
                      <span className="text-amber-300 font-medium">Remedy: </span>{v.remedy}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {result.report?.general_tips?.length > 0 && (
            <Card className="glass border-white/10 p-6" data-testid="vastu-tips">
              <h4 className="font-serif text-xl text-slate-50 mb-3">General Vastu Tips</h4>
              <ul className="space-y-2">
                {result.report.general_tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-1" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <div className="flex justify-center">
            <Button onClick={() => { setResult(null); setImageB64(null); setImageUrl(null); }} data-testid="vastu-new-btn" variant="outline" className="rounded-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
              Analyze Another Image
            </Button>
          </div>
        </div>
      )}

      <RewardedAdModal
        open={adOpen}
        onClose={() => { setAdOpen(false); pendingRetryRef.current = null; }}
        onRewarded={onRewarded}
        kind="vastu"
        title="Watch an ad to unlock 2 more Vastu analyses"
      />
    </div>
  );
}
